'use strict';

/**
 * @typedef OperationRef
 * @property {string} url
 * @property {string} operationRef
 * @property {string} [serviceId]
 */

define('vb/private/services/endpointReference',[
  'vb/private/services/serviceResolverFactory',
  'vb/private/utils',
], (
  ServiceResolverFactory,
  Utils,
) => {
  /**
   * @typedef {import('vb/private/stateManagement/container').default} Container
   * @typedef {import('vb/private/services/serviceResolverFactory').ServiceContext} ServiceContext
   */

  /**
   * class EndpointReference
   *
   * An abstraction of the ways a service endpoint (a.k.a. operation) can be references.
   * Normally, an "endpoint" property is used, which is "[<namespace>:]<service Name>/<operation ID>",
   *   where the namespace is optional.
   *
   * When creating an SDP programmatically for some Dynamic UI cases, an tuple with { url, operationRef } may be used.
   *
   * This class is meant to:
   * - represent either in a scalable way (allow more properties in the future if needed, etc).
   * - calculate a namespace when the namespace is not specified, and a container is provided for context.
   *
   */
  class EndpointReference {
    /**
     * @typedef {Object} CallingContext
     * @property {string} extensionId - The calling context extension ID
     */

    /**
     * Processes everything after the first '#', and returns operation path and method per
     * https://github.com/OAI/OpenAPI-Specification/blob/main/versions/3.1.0.md#operationref-examples
     * @param {string} operationRef example: '#/paths/~12.0~1repositories~1{username}/get'
     *  or 'https://na2.gigantic-server.com/#/paths/~12.0~1repositories~1{username}/get'
     * @returns {Object}
     */
    static processOperationRef(operationRef = '#') {
      const parts = operationRef.split('#');
      const pathParts = (parts[1] || '').split('/');

      const resolved = {
        openApi: parts[0], // part in front of the '#' is location of the openApi document
        path: '',
        method: '',
      };

      if (pathParts.length === 4) {
        resolved.path = (pathParts[2] || '').replace(/~1/g, '/');
        resolved.method = pathParts[3] || '';
      }

      return resolved;
    }

    /**
     * Creates an endpoint reference from the specified arguments.
     *
     * A string id is parsed to obtain the 'namespace', 'serviceName', and 'operationId' following this
     * pseudo expression <i>[[@]namespace:]serviceName[/operationId]</i>. If not provided 'namespace' is
     * undefined while 'serviceId' and 'operationId' are both empty strings.
     *
     * An object id is expected to provide the 'url', 'serviceId', and 'operationRef' information.
     *
     * @param { string | OperationRef } id - The endpoint identifier.
     * @param {Container|ServiceContext} [serviceContext] - typically the container which defines the scope in which
     *                                                      the endpoint is being invoked.
     * @throws {Error} if id is neither a string nor an object
     */
    constructor(id, serviceContext) {
      if (!id) {
        throw Error('Invalid ID used for endpoint reference.');
      }

      const extensionId = ServiceResolverFactory.getNamespace(serviceContext);

      // the namespace of the extension/container/"scope" in which this endpoint is being used - for example, 'extA' if
      // this endpointReference is being instantiated because of an action chain in extension 'extA'. Never undefined.
      this._containerNamespace = extensionId;

      if (typeof id === 'string') {
        // parse [namespace:]serviceId[/operationId]
        // eslint-disable-next-line max-len
        const { prefix: namespace, main: serviceId = '', suffix: operationId = '' } = Utils.parseQualifiedIdentifier(id);

        this._serviceId = serviceId;
        this._operationId = operationId;
        if (namespace && namespace[0] === '@') {
          // id = @extId:serviceId[/operationId]
          this._namespace = namespace.substring(1);
          this._programmatic = true;
        } else if (!namespace && serviceId[0] === '@') {
          // id = @serviceId[/operationId]
          this._serviceId = serviceId.substring(1);
          this._programmatic = true;
        } else {
          this._namespace = namespace;
          this._programmatic = false;
        }

        this._serviceName = this._serviceId;
        // endpoint references have moduleName only if their serviceId contains '#'
        // in that case everything before the '#' is a serviceName and after it is a moduleName
        this._moduleName = '';
        this._endpointName = '';
        if (this._serviceId) {
          const {
            main: serviceName,
            suffix: moduleName = '',
          } = Utils.parseQualifiedIdentifier(this._serviceId, { suffixToken: '#' });
          if (moduleName) {
            // [backend]#moduleName[/operationId] is a programmatic endpoint too
            this._serviceName = serviceName;
            this._moduleName = moduleName;
            this._endpointName = operationId;
            // if the endpoint does not start with '@' (it is not already detected as a programmatic one)
            // the part before the '#' is the service type
            if (!this._programmatic) {
              this._programmatic = true;
              this._serviceType = serviceName;
            }
          }
        }

        // if the endpoint has a 'namespace' we need to be able to resolve it starting from the containerNamespace.
        // In case of endpoints used in fragments, the containerNamespace is not the extension that defined the fragment
        // but the extension which referenced the fragment.
        // When there is no 'namespace' provided, the scope for resolving the endpoint is the extension ('namespace')
        // of the container which uses the endpoint
        if (namespace && serviceContext) {
          const callingExtensionId = Utils.getCallingExtensionId(serviceContext);
          if (callingExtensionId) {
            // Local namespace is the namespace of the extension where the endpoint is being referenced from.
            // It is also known as the owningExtension of fragments.
            // We need to keep reference to it as the endpoint is allowed to reference local services (those not
            // exposed to downstream extensions) in the extension that declares it.
            this._localNamespace = this._containerNamespace;
            // Container namespace in this scenario is the extension of the page that references the endpoint.
            this._containerNamespace = callingExtensionId;
          }
        }
      } else if (id.url) {
        // if its not a string, assume it's one from JET Dyn UI - url, operationRef.
        const { url, operationRef, serviceId } = id;
        this._serviceId = serviceId;
        this._serviceName = serviceId;

        this._url = url;
        this._operationRef = operationRef;

        // deconstructed operationRef
        const resolvedRef = EndpointReference.processOperationRef(operationRef);
        this.serviceUrl = resolvedRef.openApi;
        this.operationPath = resolvedRef.path;
        this.operationMethod = resolvedRef.method;
      } else {
        throw new Error(`Attempt to create an invalid EndpointReference: ${JSON.stringify(id)}`);
      }
    }

    /**
     * Adjusts programmatic EndpointReference based on the service type plugin information.
     *
     * @param {Object} options
     * @returns {EndpointReference}
     */
    // TODO: this should be done at the endpoint reference creation time
    adjustEndpoint(options) {
      if (this.isProgrammatic && options) {
        if (options.backendName) {
          this._backendName = options.backendName;
        }

        // moduleName and endpointName can be determined at the creation time (by a plugin)
        if (options.moduleName) {
          this._moduleName = options.moduleName;
          this._serviceId = `${this._serviceType}#${this._moduleName}`;
        }

        if (options.endpointName) {
          this._endpointName = options.endpointName;
        }

        // operationId may be provided by the plugin when we get plugin to resolve the endpoint def.
        if (options.operationId || options.endpointName) {
          this._operationId = options.operationId || options.endpointName;
        }
      } else if (this.url && options) {
        if (options.serviceName) {
          this._serviceId = options.serviceName;
          this._serviceName = options.serviceName;
        }
      }
      return this;
    }

    get isProgrammatic() {
      return this._programmatic;
    }

    /**
     * Namespace (extension ID) of the container in which context the endpoint reference is being resolved.
     * @returns {string}
     */
    get containerNamespace() {
      return this._containerNamespace;
    }

    /**
     * Namespace (extension ID) of the container where the endpoint reference is located.
     * @returns {string}
     */
    get referencingNamespace() {
      return this._localNamespace || this.containerNamespace;
    }

    /**
     * @returns {string|undefined}
     */
    get url() {
      return this._url;
    }

    /**
     * @returns {string|undefined}
     */
    get operationRef() {
      return this._operationRef;
    }

    /**
     * Namespace may be undefined. It does not include potential '@' character in front of the endpoint ID.
     * @returns {string|undefined}
     */
    get namespace() {
      return this._namespace;
    }

    /**
     * Operation ID. Everything after (first) '/' delimeter.
     * @returns {string}
     */
    get operationId() {
      return this._operationId;
    }

    /**
     * Service ID. Portion of the endpoint reference between the namespace and the operation ID.
     * @returns {string}
     */
    get serviceId() {
      return this._serviceId;
    }

    get qualifiedServiceId() {
      return this.namespace ? `${this.namespace}:${this.serviceId}` : this.serviceId;
    }

    /**
     * Service Name
     * @returns {string}
     */
    get serviceName() {
      return this._serviceName;
    }

    /**
     * Name of the backend reference by a programmatic endpoint: @backend/operation (eg. @faCrm/getAccounts),
     * or the backend name we resolved this endpoint reference's service type.
     */
    get backendName() {
      return (this.isProgrammatic && !this.serviceType) ? this._serviceName : this._backendName;
    }

    /**
     * Type of the service endpoint references in the shortcut format:
     *  serviceType#module/operation (eg. boss#crm/getAccounts).
     */
    get serviceType() {
      return this._serviceType;
    }

    /**
     * Module Name.
     * Endpoint references have moduleName only if their serviceId contains '#'. In that case everything
     * before the '#' is backend name and after it is module name.
     * @returns {string}
     */
    get moduleName() {
      return this._moduleName;
    }

    /**
     * Endpoint Name.
     * Endpoint references have endpointName only if their serviceId contains '#'. In that case everything
     * before the '#' is backend name and after it is module name.
     * @returns {string}
     */
    get endpointName() {
      return this._endpointName;
    }

    /**
     * Fully qualified Service ID. It contains everything in front of the operation ID.
     * If the endpoint reference was not created with specific namespace, passed in namespace is used.
     *
     * @param {string} [ns='base']
     * @returns {string}
     */
    getQualifiedServiceId(ns = this._containerNamespace) {
      // if this is a serviceType#moduleName reference, the namespace resolution is such that
      // non-namespaced endpoint references refer to the global ("base:") service types.
      // That means that we should not add container namespace if the namespace has not been set.
      if (this._moduleName) {
        return this.qualifiedServiceId;
      }
      return `${this._programmatic ? '@' : ''}${this.namespace || ns}:${this.serviceId}`;
    }

    /**
     * Fully qualified Service ID including the operation ID.
     * If the endpoint reference was not created with specific namespace, passed in namespace is used.
     *
     * @param {string} [ns='container namespace']
     * @returns {string}
     */
    getQualifiedEndpointReference(ns = this._containerNamespace) {
      return `${this.getQualifiedServiceId(ns)}/${this.operationId}`;
    }

    // never undefined: either the namespace or containerNamespace
    // useful when there is a need to obtain "the best, defined" namespace for this endpoint reference
    get derivedNamespace() {
      return this.namespace || this.containerNamespace;
    }

    /**
     * @returns {string}
     */
    toString() {
      return this._url
        ? JSON.stringify({ url: this._url, operationRef: this.operationRef, serviceId: this._serviceId })
        : this.getQualifiedEndpointReference('');
    }

    /**
     * Customize what JSON.stringify returns for this object.
     * @returns {Object}
     */
    toJSON() {
      if (this._url) {
        return {
          url: this._url,
          operationRef: this.operationRef,
          serviceId: this._serviceId,
        };
      }
      return {
        endpoint: this.getQualifiedEndpointReference(),
        containerNamespace: this._containerNamespace,
        referencingNamespace: this._localNamespace,
        programmatic: this._programmatic,
      };
    }
  }

  return EndpointReference;
});

